import { useState } from "react";
import { toast, Toaster } from "sonner";
import api from "../../lib/api";
import { ScrollReveal } from "../Reveal";

export default function BookingForm() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please provide your name and email.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/bookings", form);
      setSent(true);
      toast.success("Enquiry received. We'll be in touch shortly.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="book" className="relative bg-navy-800 py-24 md:py-40 border-t border-ivory/10" data-testid="booking-section">
      <Toaster theme="dark" position="bottom-right" />
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-8 md:gap-16">
          <ScrollReveal className="col-span-12 md:col-span-5">
            <h2 className="font-display font-light text-ivory text-5xl md:text-7xl leading-[0.9] tracking-tight">
              Begin your<br />
              <span className="italic text-gold">journey.</span>
            </h2>
            <p className="mt-8 max-w-md text-ivory/60 leading-relaxed">
              Tell us where you&rsquo;re headed and what you&rsquo;d like to achieve. A
              consultant replies within one business day &mdash; always by name,
              never by template.
            </p>
            <div className="mt-12 space-y-4 font-cinzel text-base md:text-lg tracking-[0.14em] uppercase text-ivory/75">
              <div className="flex items-center gap-4"><span className="text-gold">→</span> Response &lt; 24h</div>
              <div className="flex items-center gap-4"><span className="text-gold">→</span> Fixed-fee estimates</div>
            </div>
          </ScrollReveal>

          <ScrollReveal className="col-span-12 md:col-span-7" delay={0.15}>
            {sent ? (
              <div className="border border-gold/40 p-12 text-center" data-testid="booking-success">
                <div className="font-mono-ui text-[11px] tracking-[0.3em] text-gold uppercase mb-6">— Received —</div>
                <div className="font-display italic text-4xl md:text-5xl text-ivory">Thank you.</div>
                <p className="mt-6 text-ivory/60">We&rsquo;ll be in touch shortly.</p>
                <button
                  onClick={() => setSent(false)}
                  className="mt-10 font-mono-ui text-[11px] tracking-[0.25em] uppercase text-ivory/60 hover:text-gold transition-colors"
                  data-testid="booking-reset"
                >
                  Send another enquiry ↗
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-10" data-testid="booking-form" noValidate>
                <div>
                  <label className="block font-mono-ui text-base tracking-[0.22em] uppercase text-gold mb-3">
                    01 · Your name *
                  </label>
                  <input
                    type="text"
                    className="field-line"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Mr. / Ms. Full name"
                    required
                    data-testid="booking-input-name"
                  />
                </div>

                <div>
                  <label className="block font-mono-ui text-base tracking-[0.22em] uppercase text-gold mb-3">
                    02 · Email address *
                  </label>
                  <input
                    type="email"
                    className="field-line"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="you@example.com"
                    required
                    data-testid="booking-input-email"
                  />
                </div>

                <div>
                  <label className="block font-mono-ui text-base tracking-[0.22em] uppercase text-gold mb-3">
                    03 · Message
                  </label>
                  <textarea
                    className="field-line resize-none"
                    rows={5}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Which visa, timelines, questions..."
                    data-testid="booking-input-message"
                  />
                </div>

                <div className="flex items-center justify-between pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="inline-flex items-center gap-4 px-10 py-5 bg-gold text-navy font-mono-ui font-bold text-base md:text-lg tracking-[0.22em] uppercase hover:bg-ivory transition-colors disabled:opacity-50"
                    data-testid="booking-submit"
                  >
                    {loading ? "Sending…" : "Submit Enquiry"}
                    <span>→</span>
                  </button>
                  <span className="font-mono-ui text-sm md:text-base tracking-[0.22em] uppercase text-ivory/60">
                    * Required
                  </span>
                </div>
              </form>
            )}
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}
